if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.handleSidePillowHeightConfirmChange = this.handleSidePillowHeightConfirmChange.bind(this);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.handleOptionValueChange);
        this.sidePillowHeightConfirm?.removeEventListener('change', this.handleSidePillowHeightConfirmChange);
        this.cleanupSidePillowHeightHelper?.();
        this.cleanupSidePillowHeightHelper = null;
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      handleOptionValueChange(event) {
        if (event.target.matches('[data-sp-height-13-confirm]')) return;
        if (event.target.closest?.('[data-sp-height-helper]')) return;

        const target = this.getInputForEventTarget(event.target);
        this.updateSelectedSwatchValue(event);
        this.updateSidePillowHeightWarning();
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
          },
        });
      }

      handleSidePillowHeightConfirmChange() {
        this.sidePillowHeightConfirm?.setCustomValidity('');
        this.sidePillowHeightConfirm?.setAttribute('aria-invalid', 'false');
        this.sidePillowHeightWarning?.classList.remove('sp-height-warning--attention');
      }

      connectedCallback() {
        this.addEventListener('change', this.handleOptionValueChange);
        this.setupSidePillowHeightWarning();
        this.setupSidePillowHeightHelper();
      }

      setupSidePillowHeightHelper() {
        const helper = this.querySelector('[data-sp-height-helper]');
        if (!helper || helper.dataset.spHeightHelperBound) return;

        const openButton = helper.querySelector('[data-sp-height-helper-open]');
        const dialog = helper.querySelector('[data-sp-height-helper-dialog]');
        const applyButton = helper.querySelector('[data-sp-height-helper-apply]');
        const resultLabel = helper.querySelector('[data-sp-helper-result-label]');
        const resultHeight = helper.querySelector('[data-sp-helper-result-height]');
        const resultCopy = helper.querySelector('[data-sp-helper-result-copy]');
        const fitNote = helper.querySelector('[data-sp-helper-fit-note]');
        const personalProfile = helper.querySelector('[data-sp-helper-personal-profile]');
        const profileLength = personalProfile?.querySelector('[data-sp-helper-profile-length]');
        const profileShoulders = personalProfile?.querySelector('[data-sp-helper-profile-shoulders]');
        const profileMattress = personalProfile?.querySelector('[data-sp-helper-profile-mattress]');
        const adviceRange = helper.querySelector('[data-sp-helper-advice-range]');
        const rangeStart = adviceRange?.querySelector('[data-sp-helper-range-start]');
        const rangeEnd = adviceRange?.querySelector('[data-sp-helper-range-end]');
        const rangeCurrent = adviceRange?.querySelector('[data-sp-helper-range-current]');
        const meter = helper.querySelector('[data-sp-height-helper-meter]');
        const lengthInput = helper.querySelector('[data-sp-helper-length-input]');
        const lengthRange = helper.querySelector('[data-sp-helper-length-range]');
        const steps = [0, 1, 2, 3].map((index) => helper.querySelector(`[data-sp-guide-step="${index}"]`));
        const navButtons = [...helper.querySelectorAll('[data-sp-guide-nav]')];
        let helperTranslations = {};
        try {
          helperTranslations = JSON.parse(helper.querySelector('[data-sp-height-helper-i18n]')?.textContent || '{}');
        } catch (_error) {
          helperTranslations = {};
        }
        const storageKey = 'sp-sidepillow-height-helper-state';
        const readStoredState = () => {
          try {
            const value = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
            return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
          } catch (error) {
            return {};
          }
        };
        let storedState = readStoredState();
        const parseLength = (value) => {
          const text = String(value ?? '').trim();
          if (!/^\d+$/.test(text)) return null;
          const number = Number(text);
          return Number.isInteger(number) && number >= 150 && number <= 215 ? number : null;
        };
        const storedLength = parseLength(storedState.length);
        const state = {
          length: storedLength ?? 180,
          shoulders: ['normal', 'broad'].includes(storedState.shoulders) ? storedState.shoulders : 'normal',
          mattress: ['soft', 'medium', 'firm'].includes(storedState.mattress) ? storedState.mattress : 'medium',
        };
        // Values from an older helper session remain useful defaults, not answers.
        const answers = {
          length: storedState.answers?.length === true && storedLength !== null,
          mattress: storedState.answers?.mattress === true && ['soft', 'medium', 'firm'].includes(storedState.mattress),
          shoulders: storedState.answers?.shoulders === true && ['normal', 'broad'].includes(storedState.shoulders),
        };
        const chosen = { mattress: answers.mattress, shoulders: answers.shoulders };

        if (!openButton || !dialog || !applyButton || !resultLabel || !lengthInput || steps.some((step) => !step)) {
          return;
        }

        helper.dataset.spHeightHelperBound = 'true';
        const getHelperControls = (selector) => [...helper.querySelectorAll(selector)];

        const recommendations = {
          low: {
            key: 'low',
            heightName: helperTranslations.recommendations?.low?.heightName || 'Low',
            heightValue: helperTranslations.recommendations?.low?.heightValue || '',
            label: helperTranslations.recommendations?.low?.label || 'Recommended: Low 10 cm',
            copy: helperTranslations.recommendations?.low?.copy || '10 cm is the lowest option. If you are between two heights, choose the lower one.',
            button: helperTranslations.recommendations?.low?.button || 'Choose Low 10 cm',
          },
          medium: {
            key: 'medium',
            heightName: helperTranslations.recommendations?.medium?.heightName || 'Medium',
            heightValue: helperTranslations.recommendations?.medium?.heightValue || '',
            label: helperTranslations.recommendations?.medium?.label || 'Recommended: Medium 11.5 cm',
            copy: helperTranslations.recommendations?.medium?.copy || '11.5 cm is the middle option. If you are between two heights, choose the lower one.',
            button: helperTranslations.recommendations?.medium?.button || 'Choose Medium 11.5 cm',
          },
          high: {
            key: 'high',
            heightName: helperTranslations.recommendations?.high?.heightName || 'High',
            heightValue: helperTranslations.recommendations?.high?.heightValue || '',
            label: helperTranslations.recommendations?.high?.label || 'Recommended: High 13 cm',
            copy: helperTranslations.recommendations?.high?.copy || '13 cm is our highest option. If you are between two heights, choose the lower one.',
            button: helperTranslations.recommendations?.high?.button || 'Choose High 13 cm',
          },
        };

        let activeRecommendation = recommendations.medium;
        let reopenTimeout = null;
        let lengthHold = null;
        let suppressedStepClick = null;
        let suppressedStepClickTimeout = null;
        const allAnswered = () => answers.length && answers.mattress && answers.shoulders;
        const availableStep = () => !answers.length ? 0 : !answers.mattress ? 1 : !answers.shoulders ? 2 : 3;
        let activeStep = availableStep();

        const writeStoredState = (overrides = {}) => {
          try {
            const nextState = {
              length: state.length,
              shoulders: state.shoulders,
              mattress: state.mattress,
              answers: { ...answers },
              appliedHeight: storedState.appliedHeight || '',
              reopen: false,
              ...overrides,
            };

            if (!nextState.appliedHeight) delete nextState.appliedHeight;
            window.sessionStorage.setItem(storageKey, JSON.stringify(nextState));
            storedState = nextState;
          } catch (error) {
            storedState = {};
          }
        };

        // Product-fit test rule, not a measured pillow height or validated medical formula.
        // On an exact score boundary, retain the lower of the two pillow sizes.
        const getHeightScore = (length = state.length, shoulders = state.shoulders, mattress = state.mattress) => {
          const shoulderAdjustment = shoulders === 'broad' ? 3 : 0;
          const mattressAdjustment = mattress === 'firm' ? 4 : mattress === 'soft' ? -4 : 0;
          return Math.min(215, Math.max(150, length + shoulderAdjustment + mattressAdjustment));
        };

        const getRecommendation = (score = getHeightScore()) => {
          if (score <= 175) return recommendations.low;
          if (score <= 195) return recommendations.medium;
          return recommendations.high;
        };

        const getFitNote = (recommendation) => {
          const height = recommendation.heightValue || { low: '10 cm', medium: '11.5 cm', high: '13 cm' }[recommendation.key];
          return `Based on your answers, we recommend ${height}.`;
        };

        const getPersonalNote = (recommendation) => {
          const key = `${state.shoulders}${state.mattress[0].toUpperCase()}${state.mattress.slice(1)}`;
          const note = helperTranslations.weightedNotes?.[key];
          if (typeof note !== 'string' || !note.trim() || !note.includes('__SP_HEIGHT__')) return getFitNote(recommendation);
          const height = recommendation.heightValue || { low: '10 cm', medium: '11.5 cm', high: '13 cm' }[recommendation.key];
          return note.replaceAll('__SP_HEIGHT__', height);
        };

        const updatePersonalProfile = () => {
          if (!personalProfile) return;
          const labels = helperTranslations.personalProfile;
          const completeLabels = [
            labels?.shoulders?.normal, labels?.shoulders?.broad,
            labels?.mattress?.soft, labels?.mattress?.medium, labels?.mattress?.firm,
          ].every((label) => typeof label === 'string' && label.trim());
          const canShow = Boolean(allAnswered() && profileLength && profileShoulders && profileMattress && completeLabels);
          personalProfile.hidden = !canShow;
          if (profileLength) profileLength.textContent = canShow ? `${state.length} cm` : '';
          if (profileShoulders) profileShoulders.textContent = canShow ? labels.shoulders[state.shoulders] : '';
          if (profileMattress) profileMattress.textContent = canShow ? labels.mattress[state.mattress] : '';
        };

        const getAdviceRange = (length, shoulders, mattress) => {
          // Actual body-length interval for this advice, with the other answers fixed.
          const adjustment = (shoulders === 'broad' ? 3 : 0) + (mattress === 'firm' ? 4 : mattress === 'soft' ? -4 : 0);
          const recommendation = getRecommendation(getHeightScore(length, shoulders, mattress));
          const min = recommendation === recommendations.low ? 150
            : Math.max(150, (recommendation === recommendations.medium ? 176 : 196) - adjustment);
          const max = recommendation === recommendations.high ? 215
            : Math.min(215, (recommendation === recommendations.low ? 175 : 195) - adjustment);
          return { min, max, position: ((length - min) / (max - min)) * 100 };
        };

        const getMeterZone = (recommendation) => {
          if (recommendation === recommendations.low) return 'low';
          if (recommendation === recommendations.high) return 'high';
          return 'medium';
        };

        const getMeterMarkerPosition = () => {
          const lerp = (start, end, progress) => start + (end - start) * Math.min(1, Math.max(0, progress));
          const score = getHeightScore();
          if (score <= 175) return Math.round(lerp(6, 32, (score - 150) / 25));
          if (score <= 195) return Math.round(lerp(32, 68, (score - 175) / 20));
          return Math.round(lerp(68, 95, (score - 195) / 20));
        };

        const updateResult = () => {
          applyButton.disabled = !allAnswered();
          updatePersonalProfile();
          if (!allAnswered()) return;
          activeRecommendation = getRecommendation();
          const markerPosition = getMeterMarkerPosition(activeRecommendation);
          meter?.style.setProperty('--sp-helper-marker-position', `${markerPosition}%`);
          meter?.setAttribute('data-sp-helper-zone', getMeterZone(activeRecommendation));
          if (fitNote) fitNote.textContent = getPersonalNote(activeRecommendation);
          if (adviceRange) {
            const range = getAdviceRange(state.length, state.shoulders, state.mattress);
            adviceRange.style.setProperty('--sp-advice-position', `${range.position}%`);
            if (rangeStart) rangeStart.textContent = `${range.min} cm`;
            if (rangeEnd) rangeEnd.textContent = `${range.max} cm`;
            if (rangeCurrent) rangeCurrent.textContent = `${state.length} cm`;
          }
          if (resultHeight) {
            resultHeight.hidden = !activeRecommendation.heightValue;
            resultHeight.textContent = activeRecommendation.heightValue;
          }
          resultLabel.textContent = resultHeight && activeRecommendation.heightValue
            ? activeRecommendation.heightName
            : resultLabel.dataset.spHelperResultFormat === 'product' && activeRecommendation.heightValue
              ? `${activeRecommendation.heightName} · ${activeRecommendation.heightValue}`
              : activeRecommendation.label;
          if (resultCopy) resultCopy.textContent = activeRecommendation.copy;
          applyButton.textContent = activeRecommendation.button;
          applyButton.removeAttribute('data-sp-helper-applied');
        };

        const syncControlsFromState = () => {
          lengthInput.value = state.length;
          if (lengthRange) lengthRange.value = state.length;

          ['mattress', 'shoulders'].forEach((question) => {
            getHelperControls(`[data-sp-helper-question="${question}"]`).forEach((button) => {
              const isSelected = chosen[question] && button.dataset.spHelperValue === state[question];
              button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
              button.classList.toggle('sp-height-helper__choice--selected', isSelected);
            });
          });
        };

        const setError = (index, message = '') => {
          const error = steps[index].querySelector('[data-sp-guide-error]');
          if (error) {
            error.textContent = message;
            error.hidden = !message;
          }
          if (index === 0) lengthInput.setAttribute('aria-invalid', message ? 'true' : 'false');
        };

        const updateNavigation = () => {
          const answerNames = ['length', 'mattress', 'shoulders'];
          navButtons.forEach((button) => {
            const index = Number(button.dataset.spGuideNav);
            const answerName = answerNames[index];
            if (!answerName) return;
            button.disabled = index > availableStep();
            button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
            button.dataset.complete = answers[answerName] ? 'true' : 'false';
            if (index === activeStep) button.setAttribute('aria-current', 'step');
            else button.removeAttribute('aria-current');
            const number = button.querySelector('[data-sp-guide-number]');
            if (number) number.textContent = String(index + 1);
            const summary = button.querySelector('[data-sp-guide-summary]');
            if (!summary) return;
            const choice = getHelperControls(`[data-sp-helper-question="${answerName}"]`)
              .find((item) => item.dataset.spHelperValue === state[answerName]);
            const choiceLabel = choice?.querySelector('strong')?.textContent.trim() || choice?.textContent.trim() || '';
            summary.textContent = answers[answerName]
              ? (index === 0 ? `${state.length} cm` : choiceLabel)
              : '';
          });
        };

        const setStep = (index, { focus = true } = {}) => {
          stopLengthHold();
          activeStep = Math.max(0, Math.min(3, availableStep(), index));
          steps.forEach((step, stepIndex) => { step.hidden = stepIndex !== activeStep; });
          helper.dataset.guideStep = String(activeStep);
          updateNavigation();
          updateResult();
          if (focus && !dialog.hidden) {
            steps[activeStep].querySelector('[data-sp-guide-heading]')?.focus({ preventScroll: true });
          }
        };

        const setOpen = (isOpen) => {
          stopLengthHold();
          dialog.hidden = !isOpen;
          helper.dataset.guideOpen = isOpen ? 'true' : 'false';
          openButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          if (isOpen) {
            setStep(activeStep);
            helper.scrollIntoView({
              block: 'start',
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            });
          } else {
            writeStoredState({ reopen: false });
            openButton.focus({ preventScroll: true });
          }
        };

        const validateLength = ({ focusError = false } = {}) => {
          const value = parseLength(lengthInput.value);
          if (value === null) {
            setError(0, helperTranslations.lengthError || 'Enter a whole height between 150 and 215 cm.');
            if (focusError) lengthInput.focus({ preventScroll: true });
            return false;
          }
          state.length = value;
          lengthInput.value = value;
          if (lengthRange) lengthRange.value = value;
          setError(0);
          writeStoredState();
          return true;
        };

        const changeLength = (value, { rewriteInput = false } = {}) => {
          answers.length = false;
          const parsed = parseLength(value);
          if (parsed !== null) {
            state.length = parsed;
            if (lengthRange) lengthRange.value = parsed;
            if (rewriteInput) lengthInput.value = parsed;
          }
          setError(0);
          writeStoredState({ appliedHeight: '' });
          updateNavigation();
          updateResult();
        };

        const clearSuppressedStepClick = () => {
          window.clearTimeout(suppressedStepClickTimeout);
          suppressedStepClickTimeout = null;
          suppressedStepClick = null;
        };

        const stopLengthHold = (pointerId) => {
          const hold = lengthHold;
          if (!hold || (pointerId !== undefined && pointerId !== hold.pointerId)) return;
          lengthHold = null;
          window.clearTimeout(hold.delay);
          window.clearInterval(hold.interval);
          if (hold.repeated) {
            clearSuppressedStepClick();
            suppressedStepClick = { button: hold.button, pointerId: hold.pointerId };
            // A fresh pointerdown clears this too; keyboard clicks are never consumed.
            suppressedStepClickTimeout = window.setTimeout(clearSuppressedStepClick, 1000);
          }
          try {
            if (hold.button.hasPointerCapture?.(hold.pointerId)) hold.button.releasePointerCapture(hold.pointerId);
          } catch (_error) { /* Capture may already have ended after cancellation. */ }
        };

        const stepLength = (button) => {
          const delta = Number.parseInt(button.dataset.spHelperLengthStep, 10) || 0;
          const current = parseLength(lengthInput.value) ?? state.length;
          const next = Math.min(215, Math.max(150, current + delta));
          if (delta) changeLength(next, { rewriteInput: true });
          return next;
        };

        const handlePointerDown = (event) => {
          const button = event.target.closest?.('[data-sp-helper-length-step]');
          if (!button || button.disabled || dialog.hidden || activeStep !== 0
            || event.isPrimary === false || event.button !== 0 || lengthHold) return;
          clearSuppressedStepClick();
          const hold = { button, pointerId: event.pointerId, repeated: false, delay: null, interval: null };
          lengthHold = hold;
          try { button.setPointerCapture?.(event.pointerId); } catch (_error) { /* Window end listeners remain available. */ }
          const repeat = () => {
            if (lengthHold !== hold) return;
            if (dialog.hidden || activeStep !== 0 || button.disabled || document.hidden) {
              stopLengthHold();
              return;
            }
            hold.repeated = true;
            const next = stepLength(button);
            if (next === 150 || next === 215) stopLengthHold();
          };
          // Short presses use only the native click; no pointerdown increment.
          hold.delay = window.setTimeout(() => {
            repeat();
            if (lengthHold === hold) hold.interval = window.setInterval(repeat, 90);
          }, 400);
        };

        const handlePointerMove = (event) => {
          if (!lengthHold || event.pointerId !== lengthHold.pointerId) return;
          const rect = lengthHold.button.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right
            || event.clientY < rect.top || event.clientY > rect.bottom) stopLengthHold(event.pointerId);
        };

        const handlePointerLeave = (event) => {
          if (lengthHold && event.target === lengthHold.button) stopLengthHold(event.pointerId);
        };
        const handlePointerEnd = (event) => stopLengthHold(event.pointerId);
        const handleWindowBlur = () => stopLengthHold();
        const handleVisibilityChange = () => { if (document.hidden) stopLengthHold(); };

        const advanceStep = () => {
          if (activeStep === 0) {
            if (!validateLength({ focusError: true })) return;
            answers.length = true;
          } else if (activeStep === 1 || activeStep === 2) {
            const question = activeStep === 1 ? 'mattress' : 'shoulders';
            if (!chosen[question]) {
              setError(activeStep, helperTranslations.choiceError || 'Choose an option to continue.');
              getHelperControls(`[data-sp-helper-question="${question}"]`)[0]?.focus({ preventScroll: true });
              return;
            }
            answers[question] = true;
            setError(activeStep);
          } else return;
          writeStoredState({ appliedHeight: '' });
          setStep(activeStep + 1);
        };

        const handleInput = (event) => {
          if (event.target === lengthInput) changeLength(lengthInput.value);
          if (lengthRange && event.target === lengthRange) changeLength(lengthRange.value, { rewriteInput: true });
        };

        const handleChange = (event) => {
          // Questionnaire changes are not Shopify variant changes.
          event.stopPropagation();
        };

        const handleFocusOut = (event) => {
          if (lengthHold && event.target === lengthHold.button) stopLengthHold();
          if (event.target === lengthInput && !dialog.hidden) validateLength();
        };

        const handleKeydown = (event) => {
          if (dialog.hidden) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
          } else if (event.key === 'Enter' && event.target === lengthInput) {
            event.preventDefault();
            advanceStep();
          }
        };

        const handleClick = (event) => {
          const target = event.target;
          const answerButton = target.closest?.('[data-sp-helper-question]');

          if (target.closest?.('[data-sp-height-helper-open]')) {
            setOpen(dialog.hidden);
            return;
          }

          if (target.closest?.('[data-sp-height-helper-close]')) {
            setOpen(false);
            return;
          }

          const navButton = target.closest?.('[data-sp-guide-nav]');
          if (navButton && !navButton.disabled) {
            setStep(Number(navButton.dataset.spGuideNav));
            return;
          }
          if (target.closest?.('[data-sp-guide-next]')) {
            advanceStep();
            return;
          }
          if (target.closest?.('[data-sp-guide-back]')) {
            setStep(activeStep - 1);
            return;
          }
          if (target.closest?.('[data-sp-guide-edit-answers]')) {
            setStep(0);
            return;
          }
          const stepButton = target.closest?.('[data-sp-helper-length-step]');
          if (stepButton) {
            const isKeyboardClick = event.detail === 0;
            // Click metadata is not consistent across MouseEvent/PointerEvent producers.
            // Only a competing active pointer is excluded below; an ordinary click still steps.
            if (!isKeyboardClick && suppressedStepClick?.button === stepButton
              && (event.pointerId === undefined || event.pointerId === suppressedStepClick.pointerId)) {
              clearSuppressedStepClick();
              return;
            }
            if (!isKeyboardClick && lengthHold && event.pointerId !== lengthHold.pointerId) return;
            stepLength(stepButton);
            return;
          }

          if (answerButton) {
            const question = answerButton.dataset.spHelperQuestion;
            const value = answerButton.dataset.spHelperValue;
            const options = { mattress: ['soft', 'medium', 'firm'], shoulders: ['normal', 'broad'] };
            if (!options[question]?.includes(value)) return;
            state[question] = value;
            chosen[question] = true;
            answers[question] = false;

            getHelperControls(`[data-sp-helper-question="${question}"]`).forEach((button) => {
              const isSelected = button === answerButton;
              button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
              button.classList.toggle('sp-height-helper__choice--selected', isSelected);
            });
            setError(question === 'mattress' ? 1 : 2);
            writeStoredState({ appliedHeight: '' });
            updateNavigation();
            updateResult();
            return;
          }

          if (target.closest?.('[data-sp-height-helper-apply]')) {
            if (applyButton.disabled || !allAnswered() || activeStep !== 3) return;
            const heightLabel = this.querySelector(
              `label[data-sp-height-card][data-sp-height-key="${activeRecommendation.key}"]`
            ) || [...this.querySelectorAll('label[data-sp-height-card]')].find(
              (label) => label.dataset.spHeightName === activeRecommendation.heightName
            );
            if (!heightLabel) return;
            writeStoredState({ appliedHeight: activeRecommendation.key, reopen: false });
            // Close and restore focus before Shopify may replace the variant markup.
            setOpen(false);
            heightLabel?.click();
            this.updateSidePillowHeightWarning();
          }
        };

        const helperEvents = {
          input: handleInput,
          change: handleChange,
          focusout: handleFocusOut,
          keydown: handleKeydown,
          click: handleClick,
          pointerdown: handlePointerDown,
          pointermove: handlePointerMove,
          lostpointercapture: handlePointerEnd,
        };
        Object.entries(helperEvents).forEach(([eventName, handler]) => helper.addEventListener(eventName, handler));
        helper.addEventListener('pointerleave', handlePointerLeave, true);
        window.addEventListener('pointerup', handlePointerEnd);
        window.addEventListener('pointercancel', handlePointerEnd);
        window.addEventListener('blur', handleWindowBlur);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        let helperWasCleanedUp = false;
        const cleanupHeightHelper = () => {
          if (helperWasCleanedUp) return;
          helperWasCleanedUp = true;
          stopLengthHold();
          clearSuppressedStepClick();
          window.clearTimeout(reopenTimeout);
          Object.entries(helperEvents).forEach(([eventName, handler]) => helper.removeEventListener(eventName, handler));
          helper.removeEventListener('pointerleave', handlePointerLeave, true);
          window.removeEventListener('pointerup', handlePointerEnd);
          window.removeEventListener('pointercancel', handlePointerEnd);
          window.removeEventListener('blur', handleWindowBlur);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          dialog.hidden = true;
          helper.dataset.guideOpen = 'false';
          openButton.setAttribute('aria-expanded', 'false');
          delete helper.dataset.spHeightHelperBound;
        };
        this.cleanupSidePillowHeightHelper = cleanupHeightHelper;

        dialog.hidden = true;
        helper.dataset.guideOpen = 'false';
        openButton.setAttribute('aria-expanded', 'false');
        syncControlsFromState();
        setStep(activeStep, { focus: false });
        const heightCheckParam = new URLSearchParams(window.location.search).get('heightcheck');
        const shouldOpenFromDeepLink = heightCheckParam === 'open'
          && document.documentElement.dataset.spHeightHelperDeepLinkOpened !== 'true';
        if (shouldOpenFromDeepLink) {
          document.documentElement.dataset.spHeightHelperDeepLinkOpened = 'true';
        }
        if (storedState.reopen || shouldOpenFromDeepLink) {
          reopenTimeout = window.setTimeout(() => {
            setOpen(true);
            writeStoredState({ reopen: false });
          }, 0);
        }
      }

      setupSidePillowHeightWarning() {
        this.sidePillowHeightConfirm?.removeEventListener('change', this.handleSidePillowHeightConfirmChange);
        this.sidePillowHeightWarning = this.querySelector('[data-sp-height-13-warning]');
        if (!this.sidePillowHeightWarning) return;

        this.sidePillowHeightConfirm = this.sidePillowHeightWarning.querySelector('[data-sp-height-13-confirm]');
        if (!this.sidePillowHeightConfirm) return;

        this.sidePillowHeightConfirm.addEventListener('change', this.handleSidePillowHeightConfirmChange);

        if (!document.documentElement.dataset.spHeight13ConfirmSyncBound) {
          document.documentElement.dataset.spHeight13ConfirmSyncBound = 'true';
          document.addEventListener('change', (event) => {
            if (!event.target.matches('[data-sp-height-13-confirm]')) return;

            const formId = event.target.getAttribute('form');
            document.querySelectorAll('[data-sp-height-13-confirm]').forEach((confirm) => {
              if (confirm.getAttribute('form') !== formId) return;

              confirm.checked = event.target.checked;
              confirm.setCustomValidity('');
              confirm.setAttribute('aria-invalid', 'false');
              confirm.closest('[data-sp-height-13-warning]')?.classList.remove('sp-height-warning--attention');
            });
          });
        }

        if (!document.documentElement.dataset.spHeight13SubmitBound) {
          document.documentElement.dataset.spHeight13SubmitBound = 'true';
          document.addEventListener(
            'submit',
            (event) => {
              const formId = event.target.getAttribute('id');
              const matchingWarnings = Array.from(
                document.querySelectorAll('[data-sp-height-13-warning]:not([hidden])')
              ).filter((warning) => {
                const confirm = warning.querySelector('[data-sp-height-13-confirm]');
                return confirm && (confirm.form === event.target || confirm.getAttribute('form') === formId);
              });

              if (!matchingWarnings.length) return;

              const confirmations = matchingWarnings
                .map((warning) => warning.querySelector('[data-sp-height-13-confirm]'))
                .filter(Boolean);

              if (confirmations.some((confirm) => confirm.checked)) {
                confirmations.forEach((confirm) => {
                  confirm.setCustomValidity('');
                  confirm.setAttribute('aria-invalid', 'false');
                  confirm.closest('[data-sp-height-13-warning]')?.classList.remove('sp-height-warning--attention');
                });
                return;
              }

              event.preventDefault();
              event.stopImmediatePropagation();
              const warning = matchingWarnings.find((item) => {
                const rect = item.getBoundingClientRect();
                const style = window.getComputedStyle(item);
                return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
              }) || matchingWarnings[0];
              const confirm = warning.querySelector('[data-sp-height-13-confirm]');
              confirmations.forEach((item) => {
                item.setCustomValidity('');
                item.setAttribute('aria-invalid', 'true');
              });
              warning.classList.add('sp-height-warning--attention');
              warning.scrollIntoView({ block: 'center', behavior: 'smooth' });
              confirm.focus({ preventScroll: true });
            },
            true
          );
        }

        this.updateSidePillowHeightWarning();
      }

      updateSidePillowHeightWarning() {
        const warning = this.sidePillowHeightWarning || this.querySelector('[data-sp-height-13-warning]');
        if (!warning) return;

        const confirm = this.sidePillowHeightConfirm || warning.querySelector('[data-sp-height-13-confirm]');
        const selectedHeightCard = this.querySelector('input[type="radio"]:checked + label[data-sp-height-card]');
        const selectedHeightName = selectedHeightCard?.dataset.spHeightName?.toLowerCase();
        const isHigh13 = selectedHeightCard?.dataset.spHeightKey === 'high'
          || selectedHeightName === 'hoog'
          || selectedHeightName === 'high';

        warning.hidden = !isHigh13;
        warning.setAttribute('aria-hidden', isHigh13 ? 'false' : 'true');

        if (!confirm) return;

        confirm.disabled = !isHigh13;
        confirm.required = false;
        confirm.setAttribute('aria-required', isHigh13 ? 'true' : 'false');
        confirm.setAttribute('aria-invalid', 'false');

        if (!isHigh13) {
          confirm.checked = false;
          confirm.setCustomValidity('');
          warning.classList.remove('sp-height-warning--attention');
        }
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
